import { useMemo, useState } from 'react';
import type { Database } from '@copro/api';
import { usePermissions } from '../../../../../hooks/usePermissions';
import type {
  ActivityTimelineFeed,
  DocumentType,
  EventDatabse,
  SubjectHistoryFeed,
} from '../../../../../types';
import { getGoogleCalendarUrl } from '../../../../../utils/googleCalendar';
import type { DocumentToDelete } from '../../../../documents/external-components/DeleteDocumentModal/useDeleteDocumentModal';
import type { EditableDocument } from '../../../../documents/external-components/EditDocumentModal/useEditDocumentModal';
import { useGetDocumentsByEntity } from '../../../api/useGetDocumentsByEntity';

export type UseActivityTimelineMenuActionsProps = {
  item: ActivityTimelineFeed;
};

export type ActiveTimelineModal = 'update' | 'delete' | 'approve' | 'move' | null;

export function useActivityTimelineMenuActions(props: UseActivityTimelineMenuActionsProps) {
  const { isAdmin } = usePermissions();
  const [activeModal, setActiveModal] = useState<ActiveTimelineModal>(null);

  const isDocument = props.item.entity_type === 'document';
  const { data: entityDocs } = useGetDocumentsByEntity(
    { documentId: props.item.id ?? undefined },
    isDocument && Boolean(props.item.id)
  );
  const documentData = entityDocs?.[0];

  function handleUpdateClick() {
    setActiveModal('update');
  }

  function handleDeleteClick() {
    setActiveModal('delete');
  }

  function handleMoveClick() {
    setActiveModal('move');
  }

  function handleApproveClick() {
    setActiveModal('approve');
  }

  function handleModalClose() {
    setActiveModal(null);
  }

  function handleAddToGoogleCalendar() {
    if (!props.item.date || !props.item.title) return;
    const url = getGoogleCalendarUrl({
      title: props.item.title,
      date: props.item.date,
      end_date: props.item.end_date,
      all_day: props.item.all_day,
      description: props.item.comment,
      provider_name: props.item.provider_name,
      contact_name: props.item.contact_name,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  const event = useMemo(() => {
    return {
      id: props.item.id ?? '',
      subject_id: props.item.subject_id ?? '',
      title: props.item.title,
      date: props.item.date,
      end_date: props.item.end_date,
      all_day: props.item.all_day,
      description: props.item.comment,
      provider_name: props.item.provider_name,
      contact_name: props.item.contact_name,
      provider_id: props.item.provider_id,
      created_at: props.item.created_at ?? new Date().toISOString(),
      created_by: props.item.created_by,
      space_id: props.item.space_id ?? '',
      color: null,
      source: null,
      sub_action_id: null,
      meeting_id: null,
      is_meeting_event: false,
      isDisplayedInCalendar: true,
      isImportant: false,
    } as unknown as EventDatabse;
  }, [props.item]);

  const historyItem = useMemo(() => {
    let actionType: Database['public']['Enums']['action_type'] | null = null;
    let commType: Database['public']['Enums']['communication_type'] | null = null;

    if (props.item.entity_type === 'communication') {
      if (props.item.sub_type === 'reminder') {
        actionType = 'reminder';
      } else if (props.item.sub_type === 'email') {
        actionType = 'call';
        commType = 'email';
      } else if (props.item.sub_type === 'call') {
        actionType = 'call';
        commType = 'call';
      } else {
        actionType = 'other';
      }
    } else if (props.item.entity_type === 'quote') {
      actionType = props.item.status_text === 'approved' ? 'quote_approved' : 'quote_received';
    } else if (props.item.entity_type === 'incident') {
      actionType = 'incident';
    } else if (props.item.entity_type === 'other') {
      actionType = 'other';
    }

    return {
      id: props.item.id,
      subject_id: props.item.subject_id,
      space_id: props.item.space_id,
      provider_id: props.item.provider_id,
      provider_name: props.item.provider_name,
      contact_name: props.item.contact_name,
      title: props.item.title,
      comment: props.item.comment,
      date: props.item.date,
      created_at: props.item.created_at,
      created_by: props.item.created_by,
      amount: props.item.amount,
      has_documents: props.item.has_documents,
      all_day: props.item.all_day,
      end_date: props.item.end_date,
      source: props.item.entity_type === 'event' ? 'event' : 'action',
      action_type: actionType,
      communication_type: commType,
    } as SubjectHistoryFeed;
  }, [props.item]);

  const editableDocument = useMemo(() => {
    if (!isDocument || !props.item.id) return null;
    return {
      id: props.item.id,
      name: documentData?.name ?? props.item.title ?? 'Document',
      type: (documentData?.type ?? props.item.sub_type ?? null) as DocumentType | null,
      documentTypeId: documentData?.document_type_id ?? null,
      fileUrl: documentData?.file_url ?? '',
      providerId: props.item.provider_id ?? documentData?.provider_id ?? null,
      providerName: props.item.provider_name ?? null,
    } satisfies EditableDocument;
  }, [isDocument, props.item, documentData]);

  const documentToDelete = useMemo(() => {
    if (!isDocument || !props.item.id) return null;
    return {
      id: props.item.id,
      name: documentData?.name ?? props.item.title ?? 'Document',
      fileUrl: documentData?.file_url ?? '',
    } satisfies DocumentToDelete;
  }, [isDocument, props.item, documentData]);

  const canAddToGoogleCalendar = Boolean(
    props.item.entity_type === 'event' && props.item.date && props.item.title
  );

  const canApproveQuote = Boolean(
    props.item.entity_type === 'quote' && props.item.status_text !== 'approved'
  );

  const canMove = Boolean(
    props.item.id &&
    props.item.subject_id &&
    props.item.entity_type !== 'document'
  );

  const canEdit = Boolean(
    props.item.id && (props.item.subject_id || props.item.entity_type === 'document')
  );

  const canDelete = Boolean(props.item.id);

  return {
    isAdmin,
    activeModal,
    event,
    historyItem,
    editableDocument,
    documentToDelete,
    canAddToGoogleCalendar,
    canApproveQuote,
    canMove,
    canEdit,
    canDelete,
    handleUpdateClick,
    handleDeleteClick,
    handleMoveClick,
    handleApproveClick,
    handleAddToGoogleCalendar,
    handleModalClose,
  };
}
